import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  AlertDialogComponent,
  ThredAlertData,
} from './alert-dialog/alert-dialog.component';
import { SingleTextInputDialogComponent } from './single-text-input-dialog/single-text-input-dialog.component';
import { ConfirmDialogComponent } from './confirm-dialog/confirm-dialog.component';
import {
  BulletPointsInfoDialogComponent,
  ThredBulletPointsData,
} from './bullet-points-info-dialog/bullet-points-info-dialog/bullet-points-info-dialog.component';
import {
  DateSelectorDialogComponent,
  DateSelectorInputData,
} from './date-selector-dialog/date-selector-dialog.component';
import {
  NumberInputDialogComponent,
  NumberInputDialogData,
  NumberInputDialogResult,
} from './number-input-dialog/number-input-dialog.component';

//  note: to use this dialog service, the app must provide matdialogmodule from root
// consider putting the components in storybook?

interface ThredAlertInputData {
  title?: string;
  mainText?: string;
  subText?: string;
}

interface SingleTextInputDataWithoutBackground {
  title: string;
  label: string;
  buttonText: string;
  text: string;
}

@Injectable({
  providedIn: 'root',
})
export class ThredDialogService {
  constructor(private dialog: MatDialog) {}

  private alert(
    data: ThredAlertData,
  ): MatDialogRef<AlertDialogComponent, boolean> {
    return this.dialog.open(AlertDialogComponent, { data });
  }

  public success(
    data: ThredAlertInputData,
  ): MatDialogRef<AlertDialogComponent, boolean> {
    return this.alert({ ...data, mode: 'success' });
  }

  public errorWithDetails(
    data: ThredAlertInputData,
  ): MatDialogRef<AlertDialogComponent, boolean> {
    return this.alert({ ...data, mode: 'error' });
  }

  public error(text: string): MatDialogRef<AlertDialogComponent, boolean> {
    return this.errorWithDetails({ title: 'Error', mainText: text });
  }

  public info(
    data: ThredAlertInputData,
  ): MatDialogRef<AlertDialogComponent, boolean> {
    return this.alert({ ...data, mode: 'info' });
  }

  public bulletPointsInfo(
    data: ThredBulletPointsData,
  ): MatDialogRef<BulletPointsInfoDialogComponent, boolean> {
    return this.dialog.open(BulletPointsInfoDialogComponent, { data });
  }

  public textInput(
    data: SingleTextInputDataWithoutBackground,
    withBackgroundColor = true,
    required = true,
  ): MatDialogRef<SingleTextInputDialogComponent, string> {
    return this.dialog.open(SingleTextInputDialogComponent, {
      data: {
        ...data,
        background: withBackgroundColor,
        required,
      },
    });
  }

  public confirm(
    title?: string,
    message?: string,
  ): MatDialogRef<ConfirmDialogComponent, boolean> {
    return this.dialog.open(ConfirmDialogComponent, {
      data: { title, message },
    });
  }

  public dateSelector(
    data: DateSelectorInputData,
  ): MatDialogRef<DateSelectorDialogComponent, Date> {
    return this.dialog.open(DateSelectorDialogComponent, {
      data,
    });
  }

  public numberInput(
    title: string,
    label: string,
    placeholder?: string,
  ): MatDialogRef<NumberInputDialogComponent, NumberInputDialogResult> {
    return this.dialog.open<
      NumberInputDialogComponent,
      NumberInputDialogData,
      NumberInputDialogResult
    >(NumberInputDialogComponent, {
      data: {
        number: null,
        title,
        label,
        placeholder: placeholder ?? '',
      },
    });
  }
}
